import { useMemo, useState } from 'react';
import { CalendarRange, Loader2, CheckCircle2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useBulkGeneratePayroll, useDepartments, useEmployees } from '../../../services/httpServices/queries';
import { SearchableSelect } from '../../../components/ui/searchable-select';
import { MonthPicker } from '../../../components/ui/DatePicker';
import { HrModal } from './HrModal';
import type { BulkGeneratePayrollResult } from '../../../services/httpServices/hrService';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
const labelClass = 'block text-sm font-medium text-text mb-1';

const currentMonth = () => new Date().toISOString().slice(0, 7);

interface GeneratePayrollModalProps {
  onClose: () => void;
}

/**
 * Generates payroll for every active employee in a month. Existing records are
 * skipped unless "Overwrite" is enabled.
 */
export function GeneratePayrollModal({ onClose }: GeneratePayrollModalProps) {
  const bulkGenerate = useBulkGeneratePayroll();

  const [payPeriod, setPayPeriod] = useState(currentMonth());
  const [departmentId, setDepartmentId] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<BulkGeneratePayrollResult | null>(null);

  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments();
  const { data: employeesData, isLoading: employeesLoading } = useEmployees(1, 500, {
    status: 'active',
    payrollEnabled: 'true',
    ...(departmentId ? { departmentId } : {}),
  });

  const departmentOptions = useMemo(
    () =>
      (departmentsData?.data || []).map((department) => ({
        value: department.id,
        label: department.name,
        description: department.code,
      })),
    [departmentsData],
  );

  const employeeCount = employeesData?.data?.length ?? 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payPeriod) {
      setError('Please choose a pay period');
      return;
    }
    setError('');
    try {
      const response = await bulkGenerate.mutateAsync({
        payPeriod,
        ...(departmentId ? { departmentId } : {}),
        overwrite,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      const summary = response.data;
      setResult(summary);
      toast.success(
        `Payroll generated: ${summary.created} created, ${summary.updated} updated, ${summary.skipped} skipped`,
      );
    } catch {
      // Error toast is handled by the global MutationCache (hideToast is set).
      toast.error('Failed to generate payroll. Please try again.');
    }
  };

  const footer = result ? (
    <>
      <button
        type="button"
        onClick={() => setResult(null)}
        className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
      >
        Generate another
      </button>
      <button
        type="button"
        onClick={onClose}
        className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
      >
        Done
      </button>
    </>
  ) : (
    <>
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="generate-payroll-form"
        disabled={bulkGenerate.isPending || employeeCount === 0}
        className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
      >
        {bulkGenerate.isPending && <Loader2 size={16} className="animate-spin" />}
        <CalendarRange size={16} />
        Generate Payroll
      </button>
    </>
  );

  return (
    <HrModal
      open
      onClose={onClose}
      title="Generate Monthly Payroll"
      subtitle="Create payroll for all active, payroll-enabled employees in one run"
      icon={CalendarRange}
      maxWidth="max-w-2xl"
      footer={footer}
    >
      {result ? (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-base font-semibold text-text">Payroll Generated</p>
              <p className="text-sm text-text-muted">
                {result.periodStart} → {result.periodEnd}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Employees', value: result.total },
              { label: 'Created', value: result.created },
              { label: 'Updated', value: result.updated },
              { label: 'Skipped', value: result.skipped },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border border-border bg-background/60 p-3 text-center">
                <p className="text-2xl font-bold text-text">{stat.value}</p>
                <p className="text-xs text-text-muted mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          {result.skipped > 0 && (
            <p className="text-xs text-text-muted">
              {result.skipped} employee{result.skipped === 1 ? '' : 's'} already had payroll for this period and{' '}
              {result.skipped === 1 ? 'was' : 'were'} skipped.
            </p>
          )}
        </div>
      ) : (
        <form id="generate-payroll-form" onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-danger/10 text-danger rounded-lg p-3 text-sm font-medium border border-danger/20">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>
                Pay Period <span className="text-danger">*</span>
              </label>
              <MonthPicker
                required
                value={payPeriod}
                onChange={setPayPeriod}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Department</label>
              <SearchableSelect
                value={departmentId}
                onChange={setDepartmentId}
                options={departmentOptions}
                placeholder="All departments"
                loading={departmentsLoading}
              />
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-border bg-background/60 p-3">
            <div className="w-9 h-9 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
              {employeesLoading ? (
                <Loader2 size={16} className="animate-spin text-primary" />
              ) : (
                <Users size={16} className="text-primary" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-text">
                {employeesLoading
                  ? 'Counting employees…'
                  : `${employeeCount} payroll employee${employeeCount === 1 ? '' : 's'} in scope`}
              </p>
              <p className="text-xs text-text-muted">
                {departmentId
                  ? 'Limited to active, payroll-enabled employees in the selected department.'
                  : 'All active employees with payroll enabled across every department.'}
              </p>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
            />
            <span>
              <span className="block text-sm font-medium text-text">Overwrite existing payroll</span>
              <span className="block text-xs text-text-muted">
                Recompute salary, allowances and tax for employees who already have payroll in this period. Existing
                deductions and notes are preserved.
              </span>
            </span>
          </label>

          <div>
            <label className={labelClass}>Notes</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional note applied to newly created records"
              className={inputClass}
            />
          </div>
        </form>
      )}
    </HrModal>
  );
}
